import { useState } from "react";
import { InputText } from "primereact/inputtext";

import { Label } from "components/v2";

type AccrualDecimalInputPropsT = {
  id: string;
  label?: string;
  name?: string;
  value: number | null;
  onChange?: (e: { target: { name?: string; value: number | null } }) => void;
  error?: string;
  helperText?: string;
  isRequired?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  className?: string;
  inputClassName?: string;
};

const buildDecimalRegex = (allowNegative: boolean) =>
  new RegExp(`^${allowNegative ? "-?" : ""}(0|[1-9]\\d*)?(\\.\\d*)?$`);

const AccrualDecimalInput = ({
  id,
  label,
  name,
  value,
  onChange,
  error,
  helperText,
  isRequired,
  disabled,
  allowNegative = false,
  className,
  inputClassName,
}: AccrualDecimalInputPropsT) => {
  const [rawText, setRawText] = useState(value != null ? String(value) : "");

  const labelId = `${id}-label`;
  const helperId = `${id}-helper`;
  const describedBy = !error && helperText ? helperId : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    if (!buildDecimalRegex(allowNegative).test(raw)) return;

    setRawText(raw);

    if (raw === "") {
      onChange?.({ target: { name, value: null } });
      return;
    }

    if (raw === "-" || raw.endsWith(".")) return;

    const parsed = Number(raw);

    if (!Number.isNaN(parsed)) {
      onChange?.({ target: { name, value: parsed } });
    }
  };

  return (
    <section
      aria-labelledby={label ? labelId : undefined}
      className="flex flex-col gap-1 w-full"
    >
      {!!label && (
        <div id={labelId}>
          <Label htmlFor={id} text={label} isRequired={!!isRequired} />
        </div>
      )}
      <div className={`p-inputgroup ${className || ""}`.trim()}>
        <InputText
          id={id}
          aria-labelledby={label ? labelId : undefined}
          aria-required={isRequired || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          name={name}
          value={rawText}
          disabled={disabled}
          onChange={handleChange}
          className={`!h-5 ${error ? "p-invalid " : ""}${inputClassName ?? ""}`.trim()}
        />
      </div>
      {!!helperText && !error && (
        <small id={helperId} className="-mt-1 text-gray-500">
          {helperText}
        </small>
      )}
    </section>
  );
};

export default AccrualDecimalInput;
